import React, { useContext, useState } from 'react';
import './NoteRangeSelector.css';
import RydHeaderDialogBox from '../widgets/RydHeaderDialogBox';
import { Note } from '../../model/core';
import { NoteRangeType } from '../../model/containers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMinus,
    faPlus
} from '@fortawesome/free-solid-svg-icons';


export const FULL_RANGE: NoteRangeType = { min: 0, max: 108 };

const normalizeVisibleNoteRange = (r: NoteRangeType): NoteRangeType => {
    let lrange = r.max - r.min;
    // we display at least 1 octave
    if (lrange < 12) {
        r.min = Math.max(0, r.min - Math.floor((12 - lrange) / 2));
        r.max = Math.min(Note.MAX_NOTE_INDEX, r.max + Math.ceil((12 - lrange) / 2));
    }
    lrange = r.max - r.min;
    if (lrange < 12) {
        // we are at the edge, just extend to 12
        if (r.min === 0) {
            r.max = Math.min(Note.MAX_NOTE_INDEX, r.min + 12);
        } else if (r.max === Note.MAX_NOTE_INDEX) {
            r.min = Math.max(0, r.max - 12);
        }
    }
    return r;
}

function NoteRangeSelector({
    noteRange = FULL_RANGE,
    updateNoteRangeCB,
    getEffectiveNoteRange
}: {
    noteRange?: NoteRangeType,
    updateNoteRangeCB: (noteRange: NoteRangeType) => void,
    getEffectiveNoteRange?: () => NoteRangeType
}) {
    const [minNote, setMinNote] = useState(Note.fromIndex(noteRange.min));
    const [maxNote, setMaxNote] = useState(Note.fromIndex(noteRange.max));

    const [collapsed, setCollapsed] = React.useState(true);

    if (collapsed) {
        return (<button onClick={() => { setCollapsed(false) }}>
            [{Note.fromIndex(noteRange.min).getName()} - {Note.fromIndex(noteRange.max).getName()}]
        </button>);
    }

    return (<RydHeaderDialogBox closeCB={() => { setCollapsed(true) }}>
        <>
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>

                <span>
                    Min <RydNoteInput note={minNote} setNote={setMinNote} disableIncrease={minNote.index + 1 === maxNote.index} />
                </span>
                <span>
                    Max <RydNoteInput note={maxNote} setNote={setMaxNote} disableDecrease={minNote.index + 1 === maxNote.index} />
                </span>
            </div>
            {getEffectiveNoteRange && <button
                onClick={() => {
                    const r = normalizeVisibleNoteRange(getEffectiveNoteRange());
                    setMinNote(Note.fromIndex(r.min));
                    setMaxNote(Note.fromIndex(r.max));
                }}
            >Auto</button>}
            <button
                onClick={() => {
                    if (minNote.index < 0 || maxNote.index > Note.MAX_NOTE_INDEX || minNote.index > maxNote.index) {
                        alert('Invalid note range');
                        return;
                    }
                    updateNoteRangeCB({ min: minNote.index, max: maxNote.index });
                    setCollapsed(true);
                }}
            >Accept</button>
        </>
    </RydHeaderDialogBox>
    );
}

export default NoteRangeSelector;


export function RydNoteInput({
    note,
    setNote,
    label,
    disableIncrease = false,
    disableDecrease = false
}: {
    note: Note,
    setNote: (note: Note) => void,
    label?: string,
    disableIncrease?: boolean,
    disableDecrease?: boolean
}) {
    const [noteLocal, setNoteLocal] = useState<string>();
    const update = (note: Note) => {
        if (note.index < 0 || note.index > Note.MAX_NOTE_INDEX) return;
        setNoteLocal(undefined);
        setNote(note);
    }

    const handNoteUpdate = (e) => {
        const newNote = Note.tryFromName((e.target as HTMLInputElement).value);
        if (!newNote) {
            (e.target as HTMLInputElement).value = note.getName();
            return;
        }

        update(newNote);
        (e.target as HTMLInputElement).blur();
    }

    return (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
        <button
            type="button"
            aria-label="Decrement from note"
            disabled={note.index <= 0 || disableDecrease}
            onClick={() => {
                update(note.down(1));
            }}
        >
            <FontAwesomeIcon icon={faMinus} />
        </button>
        <input
            type="text"
            value={noteLocal || note.getName()}
            aria-label={label || "Note"}
            onChange={(e) => setNoteLocal(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    handNoteUpdate(e);
                }
            }}
            onBlur={(e) => handNoteUpdate(e)}
            style={{ width: '2em' }}
        />
        <button
            type="button"
            aria-label="Increment from note"
            disabled={note.index >= Note.MAX_NOTE_INDEX || disableIncrease}
            onClick={() => {
                update(note.up(1));
            }}
        >
            <FontAwesomeIcon icon={faPlus} />
        </button>
    </span>);
}